import React from 'react';

/**
 * Renders simple markdown syntax as React elements.
 * Supports: **bold**, *italic*, bullet lists (- or •), numbered lists (1.)
 *
 * This is an undocumented feature available to all users.
 */
export function renderSimpleMarkdown(text: string): React.ReactNode {
  // Split text into lines for list processing
  const lines = text.split('\n');
  const result: React.ReactNode[] = [];
  let currentList: { type: 'ul' | 'ol'; items: React.ReactNode[] } | null = null;

  const flushList = () => {
    if (currentList) {
      if (currentList.type === 'ul') {
        result.push(
          <ul key={`list-${result.length}`} className="my-1 ml-4 list-disc">
            {currentList.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
      } else {
        result.push(
          <ol key={`list-${result.length}`} className="my-1 ml-4 list-decimal">
            {currentList.items.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ol>
        );
      }
      currentList = null;
    }
  };

  lines.forEach((line, lineIndex) => {
    // Check for bullet list: - item or • item
    const bulletMatch = line.match(/^[\s]*[-•]\s+(.+)$/);
    if (bulletMatch) {
      if (currentList?.type !== 'ul') {
        flushList();
        currentList = { type: 'ul', items: [] };
      }
      currentList.items.push(parseInlineFormatting(bulletMatch[1]));
      return;
    }

    // Check for numbered list: 1. item, 2. item, etc.
    const numberedMatch = line.match(/^[\s]*\d+\.\s+(.+)$/);
    if (numberedMatch) {
      if (currentList?.type !== 'ol') {
        flushList();
        currentList = { type: 'ol', items: [] };
      }
      currentList.items.push(parseInlineFormatting(numberedMatch[1]));
      return;
    }

    // Not a list item - flush any pending list
    flushList();

    // Add regular line with inline formatting
    if (line.trim() === '') {
      // Empty line
      result.push(<br key={`br-${lineIndex}`} />);
    } else {
      result.push(
        <React.Fragment key={`line-${lineIndex}`}>
          {parseInlineFormatting(line)}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    }
  });

  // Flush any remaining list
  flushList();

  return <>{result}</>;
}

/**
 * Parse inline formatting: **bold** and *italic*
 */
function parseInlineFormatting(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let keyIndex = 0;

  while (remaining.length > 0) {
    // Look for **bold** first (must check before *italic* since ** contains *)
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    // Look for *italic* (but not **) - uses [^*]+ to avoid matching inside **bold**
    // Note: Avoids lookbehind (?<!) which isn't supported in Safari < 16.4
    const italicMatch = remaining.match(/\*([^*]+)\*(?!\*)/);

    // Find which comes first
    const boldIndex = boldMatch ? remaining.indexOf(boldMatch[0]) : -1;
    const italicIndex = italicMatch ? remaining.indexOf(italicMatch[0]) : -1;

    // Determine which pattern to process first
    let matchToUse: RegExpMatchArray | null = null;
    let matchIndex = -1;
    let isBold = false;

    if (boldIndex !== -1 && (italicIndex === -1 || boldIndex <= italicIndex)) {
      matchToUse = boldMatch;
      matchIndex = boldIndex;
      isBold = true;
    } else if (italicIndex !== -1) {
      matchToUse = italicMatch;
      matchIndex = italicIndex;
      isBold = false;
    }

    if (matchToUse && matchIndex !== -1) {
      // Add text before the match
      if (matchIndex > 0) {
        parts.push(remaining.substring(0, matchIndex));
      }

      // Add the formatted text
      if (isBold) {
        parts.push(
          <strong key={`bold-${keyIndex++}`}>{matchToUse[1]}</strong>
        );
      } else {
        parts.push(<em key={`italic-${keyIndex++}`}>{matchToUse[1]}</em>);
      }

      // Continue with the rest
      remaining = remaining.substring(matchIndex + matchToUse[0].length);
    } else {
      // No more matches, add remaining text
      parts.push(remaining);
      break;
    }
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}
